import { ChevronDown, Search, Check } from 'lucide-react';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { IMaskInput } from 'react-imask';

import type { PhoneCountry } from '../../lib/phone/countries';
import { ALL_COUNTRIES } from '../../lib/phone/countries';
import { parseE164, buildE164, validatePhone } from '../../lib/phone/utils';

import {
  useValueEffect,
  useMaskUpdateEffect,
  useAutofillEffect,
  useClickOutsideEffect,
  useSearchFocusEffect,
  useDropdownPortalPosition,
} from './effects';
import { handleCountrySelect, handleInputChange } from './handlers';
import { PhoneFieldProps } from './types';

export type { PhoneFieldProps }

export default function PhoneField({
  value,
  onChange,
  countries = ALL_COUNTRIES,
  defaultCountryIso2 = 'US',
  disabled = false,
  className = '',
  id,
  name,
}: PhoneFieldProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(() => {
    // Initialize from value if provided
    if (value) {
      const parsed = parseE164(value, countries);
      return parsed.country || countries.find((c) => c.iso2 === defaultCountryIso2) || countries[0];
    }
    return countries.find((c) => c.iso2 === defaultCountryIso2) || countries[0];
  });
  const [nationalNumber, setNationalNumber] = useState(() => {
    if (value) {
      const parsed = parseE164(value, countries);
      return parsed.national;
    }
    return '';
  });
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const isProcessingAutofillRef = useRef(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Filter countries based on search (countries are already sorted alphabetically)
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return countries;
    const query = searchQuery.toLowerCase();
    const filtered = countries.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.callingCode.includes(query) ||
        c.iso2.toLowerCase().includes(query)
    );
    // Ensure filtered results are also sorted alphabetically
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [countries, searchQuery]);

  // Parse value prop changes
  useValueEffect(value, countries, setSelectedCountry, setNationalNumber)

  // Force update mask value when country or national number changes (for autofill)
  useMaskUpdateEffect(selectedCountry, nationalNumber, inputRef)

  // Handle browser autofill - intercept before mask applies
  useAutofillEffect(selectedCountry, countries, isProcessingAutofillRef, setSelectedCountry, setNationalNumber, onChange, inputRef)

  // Close dropdown on outside click
  useClickOutsideEffect(isDropdownOpen, dropdownRef, buttonRef, setIsDropdownOpen, setSearchQuery, setFocusedIndex)

  // Focus search input when dropdown opens
  useSearchFocusEffect(isDropdownOpen, searchInputRef)

  const dropdownPosition = useDropdownPortalPosition(isDropdownOpen, anchorRef)

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isDropdownOpen) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsDropdownOpen(true);
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setIsDropdownOpen(false);
          setSearchQuery('');
          setFocusedIndex(-1);
          buttonRef.current?.focus();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev < filteredCountries.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < filteredCountries.length) {
            handleCountrySelectWrapper(filteredCountries[focusedIndex]);
          }
          break;
      }
    },
    [isDropdownOpen, filteredCountries, focusedIndex]
  );

  const handleCountrySelectWrapper = (country: PhoneCountry) => {
    handleCountrySelect(country, setSelectedCountry, setNationalNumber, setIsDropdownOpen, setSearchQuery, setFocusedIndex, inputRef, onChange)
  }

  const handleInputChangeWrapper = (unmasked: string) => {
    handleInputChange(unmasked, selectedCountry, countries, setSelectedCountry, setNationalNumber, onChange)
  }

  // Create placeholder from mask (replace 0 with •)
  const placeholder = selectedCountry.mask.replace(/0/g, '•');

  // Count digits
  const digitCount = nationalNumber.length;
  const maxDigits = selectedCountry.nationalDigits;
  const e164 = buildE164(selectedCountry, nationalNumber);
  const isValid = e164 ? validatePhone(e164) : false;

  const dropdownPanel =
    isDropdownOpen && dropdownPosition ? (
      <div
        ref={dropdownRef}
        data-testid="phone-country-dropdown"
        className="fixed z-[70] bg-white border border-gray-300 rounded-md shadow-lg flex flex-col"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          maxHeight: dropdownPosition.maxHeight,
        }}
        role="listbox"
      >
        <div className="p-2 border-b border-gray-200 shrink-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setFocusedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search countries..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0">
          {filteredCountries.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">No countries found</div>
          ) : (
            filteredCountries.map((country, index) => (
              <button
                key={country.iso2}
                type="button"
                onClick={() => handleCountrySelectWrapper(country)}
                onMouseEnter={() => setFocusedIndex(index)}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors flex items-center justify-between ${
                  selectedCountry.iso2 === country.iso2 ? 'bg-green-50' : ''
                } ${focusedIndex === index ? 'bg-gray-50' : ''}`}
                role="option"
                aria-selected={selectedCountry.iso2 === country.iso2}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium text-gray-900 truncate">{country.name}</span>
                  <span className="text-gray-500 shrink-0">+{country.callingCode}</span>
                </div>
                {selectedCountry.iso2 === country.iso2 && (
                  <Check className="w-4 h-4 text-green-600 shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      </div>
    ) : null

  return (
    <div ref={anchorRef} className={`relative ${className}`}>
      <div className="flex overflow-hidden rounded-lg border border-border-default bg-white focus-within:ring-2 focus-within:ring-interactive-focus-ring">
        {/* Country Selector Button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="flex shrink-0 items-center gap-2 border-r border-border-default bg-surface-muted px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-muted/80 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
        >
          <span>+{selectedCountry.callingCode}</span>
          <ChevronDown className={`h-4 w-4 text-text-muted transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Phone Input */}
        <div className="relative min-w-0 flex-1">
          <IMaskInput
            mask={selectedCountry.mask}
            unmask={true}
            value={nationalNumber}
            onAccept={(value: string) => {
              // onAccept receives the unmasked value
              // Check if this might be a full number with country code (from autofill)
              const digitsOnly = value.replace(/\D/g, '');
              
              if (digitsOnly.length > selectedCountry.nationalDigits) {
                // Try to parse as full number with country code
                let fullNumber = digitsOnly;
                if (!fullNumber.startsWith('+')) {
                  fullNumber = '+' + fullNumber;
                }
                
                try {
                  const parsed = parseE164(fullNumber, countries);
                  if (parsed.country && parsed.national) {
                    // Use flushSync for immediate synchronous update
                    flushSync(() => {
                      setSelectedCountry(parsed.country!);
                      setNationalNumber(parsed.national);
                    });
                    
                    const e164 = buildE164(parsed.country!, parsed.national);
                    const isValid = e164 ? validatePhone(e164) : false;
                    
                    if (onChange) {
                      onChange(e164, {
                        countryIso2: parsed.country!.iso2,
                        callingCode: parsed.country!.callingCode,
                        national: parsed.national,
                        isValid,
                      });
                    }
                    
                    // Immediately update the mask instance to preserve the value
                    // Use requestAnimationFrame to ensure DOM is ready
                    requestAnimationFrame(() => {
                      if (inputRef.current) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const maskInstance = (inputRef.current as any).mask;
                        if (maskInstance) {
                          // Update mask pattern if country changed
                          if (maskInstance.mask !== parsed.country!.mask) {
                            maskInstance.updateOptions({ mask: parsed.country!.mask });
                          }
                          // Set the value
                          maskInstance.value = parsed.national;
                          maskInstance.updateValue();
                        }
                      }
                    });
                    
                    return; // Don't process as normal input
                  }
                } catch {
                  // If parsing fails, continue with normal flow
                }
              }
              
              // Normal flow - process as national number only
              handleInputChangeWrapper(value);
            }}
            placeholder={placeholder}
            disabled={disabled}
            id={id}
            name={name}
            autoComplete="tel"
            inputRef={(el) => {
              inputRef.current = el;
            }}
            prepare={(str) => {
              // Remove non-digits
              const digits = str.replace(/\D/g, '');
              
              // If it looks like a full number with country code (longer than national digits),
              // try to parse it as full number BEFORE mask applies
              if (digits.length > selectedCountry.nationalDigits) {
                // Try to parse as full number
                let fullNumber = digits;
                if (!fullNumber.startsWith('+')) {
                  fullNumber = '+' + fullNumber;
                }
                
                try {
                  const parsed = parseE164(fullNumber, countries);
                  if (parsed.country && parsed.national) {
                    // CRITICAL: Update country immediately using flushSync for synchronous update
                    // This must happen before the mask processes the value
                    flushSync(() => {
                      setSelectedCountry(parsed.country!);
                      setNationalNumber(parsed.national);
                    });
                    
                    const e164 = buildE164(parsed.country, parsed.national);
                    const isValid = e164 ? validatePhone(e164) : false;
                    
                    // Call onChange immediately
                    if (onChange) {
                      onChange(e164, {
                        countryIso2: parsed.country.iso2,
                        callingCode: parsed.country.callingCode,
                        national: parsed.national,
                        isValid,
                      });
                    }
                    
                    // Immediately update the mask instance to preserve the value
                    // Use requestAnimationFrame to ensure DOM is ready
                    requestAnimationFrame(() => {
                      if (inputRef.current) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const maskInstance = (inputRef.current as any).mask;
                        if (maskInstance) {
                          // Update mask pattern if country changed
                          if (maskInstance.mask !== parsed.country.mask) {
                            maskInstance.updateOptions({ mask: parsed.country.mask });
                          }
                          // Set the value
                          maskInstance.value = parsed.national;
                          maskInstance.updateValue();
                        }
                      }
                    });
                    
                    // Return only national number for the mask to process
                    return parsed.national;
                  }
                } catch {
                  // If parsing fails, return all digits and let onAccept handle it
                }
                
                // Return all digits - onAccept will handle parsing
                return digits;
              }
              
              // Otherwise, limit to national digits
              return digits.slice(0, selectedCountry.nationalDigits);
            }}
            className="w-full min-w-0 border-0 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>

      {/* Helper Text */}
      <div className="mt-1 flex items-center gap-2 text-xs text-text-muted">
        <span className={digitCount === maxDigits && isValid ? 'text-brand-primary' : undefined}>
          {digitCount}/{maxDigits} digits
        </span>
        {digitCount === maxDigits && isValid && (
          <span className="font-medium text-brand-primary flex items-center gap-1">
            <Check className="w-3 h-3" />
            Valid
          </span>
        )}
      </div>

      {typeof document !== 'undefined' && dropdownPanel
        ? createPortal(dropdownPanel, document.body)
        : null}
    </div>
  );
}
